/**
 * Toast — ephemeral notification system for non-blocking feedback.
 *
 * Exposes a ToastProvider and a useToast() hook returning { error, info, success }.
 * Used to surface socket 'error' events and async failures without alert().
 * Toasts auto-dismiss after a timeout and are announced via aria-live.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import styles from './Toast.module.css';

export type ToastVariant = 'info' | 'success' | 'error';

export interface ToastEntry {
  id: number;
  variant: ToastVariant;
  message: string;
}

export interface ToastApi {
  /** Show an informational toast. */
  info: (message: string) => void;
  /** Show a success toast. */
  success: (message: string) => void;
  /** Show an error toast (used for socket errors and failed actions). */
  error: (message: string) => void;
}

/** Toast auto-dismiss duration in milliseconds. */
const TOAST_TIMEOUT_MS = 5000;

const ToastContext = createContext<ToastApi | null>(null);

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Provide a toast queue to descendant components. Mount once near the app root.
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, variant, message }]);
      window.setTimeout(() => remove(id), TOAST_TIMEOUT_MS);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      info: (m: string) => push('info', m),
      success: (m: string) => push('success', m),
      error: (m: string) => push('error', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.region} role="region" aria-label="Notifications" aria-live="polite">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  toast: ToastEntry;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const variantClass = styles[toast.variant] ?? styles.info;
  return (
    <div className={`${styles.toast} ${variantClass}`} role="status">
      <span className={styles.message}>{toast.message}</span>
      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss notification"
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Access the toast API. Must be used within a <ToastProvider>.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // WHY: throwing keeps the contract honest; the provider is mounted in App.
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
